/**
 * @fileoverview ClubOS — main application shell
 * Handles routing, theme, RTL support, Sidenav rendering, and auth guards.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Sidenav from "examples/Sidenav";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import routes from "routes";
import { useSoftUIController, setMiniSidenav } from "context";
import { useAuth } from "context/AuthContext";
import { ProtectedRoute } from "components/ProtectedRoute";
import { CLUB_NAME } from "utils/indiaLocale";

const brand = `${process.env.PUBLIC_URL}/images/Athelite.png`;
void SoftBox;

// Auth routes that do NOT need a guard (sign-in, sign-up)
const AUTH_ROUTES = ["/authentication/sign-in", "/authentication/sign-up"];

export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // PWA install prompt
  const deferredPrompt = useRef(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setShowInstallBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    // Hide banner if already installed
    window.addEventListener("appinstalled", () => setShowInstallBanner(false));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === "accepted") deferredPrompt.current = null;
    setShowInstallBanner(false);
  };

  const { isAuthenticated, loading } = useAuth();

  useMemo(() => {
    setRtlCache(createCache({ key: "rtl", stylisPlugins: [rtlPlugin] }));
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) { setMiniSidenav(dispatch, false); setOnMouseEnter(true); }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) { setMiniSidenav(dispatch, true); setOnMouseEnter(false); }
  };

  useEffect(() => { document.body.setAttribute("dir", direction); }, [direction]);
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  /**
   * Build <Route> elements. Public auth routes render directly.
   * All other routes are wrapped in <ProtectedRoute> with optional permission.
   */
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (!route.route) return null;

      const isAuthRoute = AUTH_ROUTES.includes(route.route);

      if (isAuthRoute) {
        // Auth pages: redirect to dashboard if already logged in
        return (
          <Route
            exact
            path={route.route}
            element={
              isAuthenticated
                ? <Navigate to="/academy/dashboard" replace />
                : route.component
            }
            key={route.key}
          />
        );
      }

      // All other routes: require authentication (+ optional permission)
      return (
        <Route
          exact
          path={route.route}
          element={
            <ProtectedRoute permission={route.permission} anyOf={route.anyOf} adminOnly={!!route.adminOnly}>
              {route.component}
            </ProtectedRoute>
          }
          key={route.key}
        />
      );
    });

  // While checking the stored token, render nothing to avoid a flash.
  if (loading) return null;

  const sidenavComponent = (
    <Sidenav
      color={sidenavColor}
      brand={brand}
      brandName={CLUB_NAME}
      routes={routes}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    />
  );

  const defaultRedirect = isAuthenticated
    ? <Navigate to="/academy/dashboard" />
    : <Navigate to="/authentication/sign-in" />;

  const content = (
    <>
      {layout === "dashboard" && isAuthenticated && sidenavComponent}
      <Routes>
        {getRoutes(routes)}
        <Route path="/dashboard" element={<Navigate to="/academy/dashboard" />} />
        <Route path="*" element={defaultRedirect} />
      </Routes>
    </>
  );

  const installBanner = (
    <Snackbar
      open={showInstallBanner}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{
        "& .MuiSnackbarContent-root": {
          background: "linear-gradient(135deg,#1a73e8 0%,#0d47a1 100%)",
          borderRadius: "12px",
          minWidth: 320,
          boxShadow: "0 8px 24px rgba(26,115,232,0.35)",
          px: 2,
        },
      }}
      message={
        <SoftBox display="flex" alignItems="center" gap={1.5}>
          <Icon sx={{ color: "#fff", fontSize: "1.4rem !important" }}>install_mobile</Icon>
          <SoftBox>
            <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#fff", display: "block" }}>
              Install Athelite
            </SoftTypography>
            <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
              Install the app for the best experience
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      }
      action={
        <SoftBox display="flex" gap={1}>
          <Button
            size="small"
            onClick={handleInstall}
            sx={{
              color: "#fff", fontWeight: 700, fontSize: "0.75rem",
              background: "rgba(255,255,255,0.2)", borderRadius: "8px",
              px: 1.5, "&:hover": { background: "rgba(255,255,255,0.3)" },
            }}
          >
            Install
          </Button>
          <Button
            size="small"
            onClick={() => setShowInstallBanner(false)}
            sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.7rem", minWidth: 0 }}
          >
            Not now
          </Button>
        </SoftBox>
      }
    />
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {content}
        {installBanner}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {content}
      {installBanner}
    </ThemeProvider>
  );
}
